/**
 * Formulários dinâmicos e versionados, compartilhados entre os módulos (Jurídico, ADR 0023;
 * Facilities, ADR 0027). Cada módulo escolhe os tipos de campo que aceita; a mesma definição valida
 * o que a administração salva e o que a pessoa responde (na tela e no servidor).
 */
import { z } from 'zod';
import { addressIssue } from './address.ts';
import { isValidCnpj, normalizeTaxId } from './partners.ts';
import { localizedText } from './schemas.ts';

const optionalHelp = z
  .object({ 'pt-BR': z.string().trim().max(500), en: z.string().trim().max(500) })
  .optional();

/** Campo de formulário, como fica guardado na versão (o tipo vem da lista do módulo). */
export interface FormFieldDef {
  key: string;
  type: string;
  label: { 'pt-BR': string; en: string };
  help?: { 'pt-BR': string; en: string };
  required: boolean;
  options?: { value: string; label: { 'pt-BR': string; en: string } }[];
  maxLength?: number;
  /** Limites do número (checklists de manutenção): fora deles é não conformidade, não erro. */
  min?: number;
  max?: number;
  showIf?: { field: string; equals: string | boolean };
  /** Formato conferido no texto (ex.: CNPJ com dígitos verificadores). */
  format?: 'cnpj';
  /** Documento exigido dispensado quando o contrato original assinado já está arquivado. */
  waivedBySignedContract?: boolean;
}
export interface FormSchemaDef {
  fields: FormFieldDef[];
  /**
   * Dados financeiros da etapa de identificação: none (sem valor, centro de custo nem rateio),
   * optional (valor e centro de custo opcionais) ou contract (contrato: centro de custo ou rateio
   * obrigatório, gestor do contrato). Sem a marcação, vale optional (como antes).
   */
  financial?: FormFinancialProfile;
}
export const FORM_FINANCIAL_PROFILES = ['none', 'optional', 'contract'] as const;
export type FormFinancialProfile = (typeof FORM_FINANCIAL_PROFILES)[number];

/**
 * Validações de um módulo: o campo (chave, tipo, rótulos, ajuda, obrigatório, opções, tamanho e
 * condição sobre um campo anterior) e o formulário inteiro (chaves únicas, condições válidas).
 */
export function defineFormSchemas<const T extends readonly [string, ...string[]]>(types: T) {
  const field = z
    .object({
      key: z
        .string()
        .regex(
          /^[a-z][a-z0-9_]{1,39}$/,
          'Use letras minúsculas, números ou _ (começando por letra)',
        ),
      type: z.enum(types),
      label: localizedText,
      help: optionalHelp,
      required: z.boolean().default(false),
      options: z
        .array(z.object({ value: z.string().trim().min(1).max(60), label: localizedText }))
        .max(100)
        .optional(),
      maxLength: z.number().int().min(1).max(10000).optional(),
      min: z.number().finite().optional(),
      max: z.number().finite().optional(),
      format: z.enum(['cnpj']).optional(),
      waivedBySignedContract: z.boolean().optional(),
      /** Mostra o campo só quando outro campo (anterior) tem este valor. */
      showIf: z
        .object({
          field: z.string().min(1),
          equals: z.union([z.string().min(1), z.boolean()]),
        })
        .optional(),
    })
    .refine((f) => !['select', 'multi_select'].includes(f.type) || (f.options?.length ?? 0) > 0, {
      path: ['options'],
      message: 'Informe as opções da lista',
    })
    .refine((f) => !f.options || new Set(f.options.map((o) => o.value)).size === f.options.length, {
      path: ['options'],
      message: 'Opções repetidas',
    })
    .refine((f) => f.type === 'number' || (f.min === undefined && f.max === undefined), {
      path: ['min'],
      message: 'Limites só para números',
    })
    .refine((f) => f.min === undefined || f.max === undefined || f.min <= f.max, {
      path: ['max'],
      message: 'O máximo não pode ser menor que o mínimo',
    });
  type Field = z.infer<typeof field>;

  const schema = z
    .object({
      fields: z.array(field).max(80),
      financial: z.enum(FORM_FINANCIAL_PROFILES).optional(),
    })
    .superRefine((s, ctx) => {
      const seen = new Map<string, Field>();
      s.fields.forEach((f, i) => {
        if (seen.has(f.key)) {
          ctx.addIssue({ code: 'custom', path: ['fields', i, 'key'], message: 'Chave repetida' });
        }
        if (f.showIf) {
          const target = seen.get(f.showIf.field);
          if (!target) {
            ctx.addIssue({
              code: 'custom',
              path: ['fields', i, 'showIf'],
              message: 'A condição precisa apontar para um campo anterior',
            });
          } else if (target.type === 'checkbox' ? typeof f.showIf.equals !== 'boolean' : false) {
            ctx.addIssue({
              code: 'custom',
              path: ['fields', i, 'showIf'],
              message: 'Para caixa de marcação, a condição é marcada ou não marcada',
            });
          }
        }
        seen.set(f.key, f);
      });
    });
  return { field, schema };
}

/** O campo aparece com as respostas atuais? (condição sobre um campo anterior) */
export function formFieldVisible(
  field: Pick<FormFieldDef, 'showIf'>,
  values: Record<string, unknown>,
): boolean {
  if (!field.showIf) return true;
  const v = values[field.showIf.field];
  return Array.isArray(v) ? v.includes(field.showIf.equals) : v === field.showIf.equals;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DECIMAL_RE = /^\d{1,15}(\.\d{1,4})?$/;

/**
 * Confere as respostas contra o formulário: obrigatórios visíveis, tipos, opções e campos
 * desconhecidos. Campos escondidos pela condição não podem ter resposta. Os tipos de cadastro
 * (pessoa, área, local...) guardam o id; a existência e o acesso são conferidos no servidor.
 * Devolve os problemas no formato { path, message } usado pelas telas.
 */
export function validateFormFieldValues(
  schema: FormSchemaDef,
  values: Record<string, unknown>,
): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  const known = new Set(schema.fields.map((f) => f.key));
  for (const key of Object.keys(values)) {
    if (!known.has(key)) issues.push({ path: key, message: 'Campo que não existe no formulário' });
  }
  for (const f of schema.fields) {
    if (f.type === 'attachment') continue; // documentos exigidos: conferidos nos anexos
    const v = values[f.key];
    const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    if (!formFieldVisible(f, values)) {
      if (!empty) issues.push({ path: f.key, message: 'Campo escondido não pode ter resposta' });
      continue;
    }
    if (empty) {
      // Caixa de marcação obrigatória precisa estar marcada (ciência, declaração).
      if (f.required) issues.push({ path: f.key, message: 'Obrigatório' });
      continue;
    }
    const bad = (message: string) => issues.push({ path: f.key, message });
    switch (f.type) {
      case 'text':
      case 'long_text': {
        const max = f.maxLength ?? (f.type === 'text' ? 500 : 10000);
        if (typeof v !== 'string') bad('Texto inválido');
        else if (v.length > max) bad(`Use até ${max} caracteres`);
        else if (f.format === 'cnpj' && !isValidCnpj(normalizeTaxId(v))) bad('CNPJ inválido');
        break;
      }
      case 'number':
        if (typeof v !== 'number' || !Number.isFinite(v)) bad('Número inválido');
        break;
      case 'date':
        if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) bad('Data inválida');
        break;
      case 'money': {
        const m = v as { amount?: unknown; currency?: unknown };
        if (
          typeof m !== 'object' ||
          typeof m.amount !== 'string' ||
          !DECIMAL_RE.test(m.amount) ||
          typeof m.currency !== 'string' ||
          !/^[A-Z]{3}$/.test(m.currency)
        )
          bad('Informe valor e moeda');
        break;
      }
      case 'select':
        if (!f.options?.some((o) => o.value === v)) bad('Escolha uma das opções');
        break;
      case 'conformity':
        // Checklists de manutenção: conforme, não conforme ou não se aplica.
        if (v !== 'ok' && v !== 'nok' && v !== 'na') {
          bad('Escolha conforme, não conforme ou não se aplica');
        }
        break;
      case 'multi_select':
        if (!Array.isArray(v) || v.some((x) => !f.options?.some((o) => o.value === x)))
          bad('Escolha entre as opções');
        break;
      case 'checkbox':
        if (typeof v !== 'boolean') bad('Marque ou desmarque');
        else if (f.required && v !== true) bad('Obrigatório');
        break;
      case 'address': {
        const message = addressIssue(v, f.required);
        if (message) bad(message);
        break;
      }
      case 'contract_ref': {
        // Contrato do cadastro (id) ou a referência digitada (número CTR ou o do próprio contrato).
        const c = v as { contractId?: unknown; reference?: unknown };
        const okId =
          c.contractId == null || (typeof c.contractId === 'string' && UUID_RE.test(c.contractId));
        const okRef =
          c.reference == null ||
          (typeof c.reference === 'string' && c.reference.trim().length <= 100);
        if (
          typeof c !== 'object' ||
          !okId ||
          !okRef ||
          (!c.contractId && !String(c.reference ?? '').trim())
        ) {
          bad('Escolha o contrato ou informe o número');
        }
        break;
      }
      default:
        // Cadastros corporativos: o id; a existência e o acesso são conferidos no servidor.
        if (typeof v !== 'string' || !UUID_RE.test(v)) bad('Escolha um item da lista');
    }
  }
  return issues;
}
